<template>
    <div class="assign-student-lookup">
        <v-layout row wrap class="assign-student-fields">
            <v-flex xs7 class="pr-1">
                <v-text-field
                    v-model="nameSearch"
                    label="Student name"
                    placeholder="First and/or last"
                    prepend-icon="mdi-account-search"
                    hide-details
                    clearable
                    @input="onSearch"
                ></v-text-field>
            </v-flex>
            <v-flex xs5 class="pl-1">
                <v-text-field
                    v-model="dobSearch"
                    label="Student DOB"
                    placeholder="MM/DD/YYYY"
                    mask="##/##/####"
                    hide-details
                    clearable
                    @input="onSearch"
                ></v-text-field>
            </v-flex>
        </v-layout>

        <div v-if="!selected && loading" class="grey--text mt-2">Searching students...</div>
        <div
            v-else-if="!selected && canSearch && !results.length"
            class="grey--text mt-2"
        >
            No matching students
        </div>

        <div v-if="!selected && results.length" class="assign-student-results mt-2">
            <div class="caption grey--text mb-1">
                {{ results.length }} result{{ results.length === 1 ? "" : "s" }}
            </div>
            <v-card
                v-for="student in results"
                :key="student.studentID + '-' + student.accountID"
                class="assign-student-card pa-2 mb-2"
                :class="{
                    'assign-student-card--selected':
                        selected && selected.studentID === student.studentID,
                }"
                @click.native="select(student)"
            >
                <div class="font-weight-medium">
                    {{ studentDisplayName(student) }}
                    <span v-if="student.class" class="grey--text font-weight-regular">
                        · {{ student.class }}
                    </span>
                </div>
                <div class="caption mt-1">
                    <span
                        v-if="student.dob"
                        :class="{ 'assign-student-dob--match': searchingByDob }"
                    >{{ formatDob(student.dob) }}</span>
                    <span v-if="student.accountID">
                        <span v-if="student.dob"> · </span>
                        Account {{ student.accountID }}
                    </span>
                    <span v-if="student.name">
                        · {{ student.name }}
                    </span>
                    <span v-if="student.homePhone">
                        · {{ formatPhone(student.homePhone) }}
                    </span>
                    <span v-if="student.cellPhone">
                        · {{ formatPhone(student.cellPhone) }}
                    </span>
                </div>
                <div v-if="student.address1" class="caption grey--text">
                    {{ student.address1 }}
                </div>
            </v-card>
        </div>

        <v-card
            v-if="selected && selected.accountID"
            class="assign-student-selected mt-2"
        >
            <div class="assign-student-selected-header">
                <span class="assign-student-selected-label">Selected student</span>
                <v-btn
                    icon
                    small
                    class="assign-student-selected-clear my-0 py-0"
                    title="Remove selected student"
                    @click.stop="removeSelected"
                >
                    <v-icon small color="#546e7a">mdi-close</v-icon>
                </v-btn>
            </div>
            <div class="assign-student-selected-body">
                <div class="assign-student-selected-name">
                    {{ studentDisplayName(selected) }}
                    <span v-if="selected.class" class="assign-student-selected-meta">
                        · {{ selected.class }}
                    </span>
                </div>
                <div class="assign-student-selected-meta">
                    <span
                        v-if="selected.dob"
                        :class="{ 'assign-student-dob--match': searchingByDob }"
                    >{{ formatDob(selected.dob) }}</span>
                    <span v-if="selected.accountID">
                        <span v-if="selected.dob"> · </span>
                        Account {{ selected.accountID }}
                    </span>
                </div>
                <div v-if="selected.name" class="assign-student-selected-meta">
                    {{ selected.name }}
                    <span v-if="selected.hname"> · {{ selected.hname }}</span>
                </div>
                <div
                    v-if="selected.address1"
                    class="assign-student-selected-meta"
                >
                    {{ selected.address1 }}
                </div>
                <div
                    v-if="selected.homePhone || selected.cellPhone"
                    class="assign-student-selected-meta"
                >
                    {{ formatPhone(selected.homePhone) }}
                    <span v-if="selected.homePhone && selected.cellPhone"> · </span>
                    {{ formatPhone(selected.cellPhone) }}
                </div>
            </div>
        </v-card>
    </div>
</template>
<script>
import moment from "moment";
import { formatPhoneNumber } from "../../utils/helpers.js";

export default {
    name: "AssignStudentLookup",
    props: {
        value: String,
    },
    data() {
        return {
            nameSearch: "",
            dobSearch: "",
            loading: false,
            results: [],
            selected: null,
            searchTimer: null,
        };
    },
    computed: {
        canSearch() {
            var name = (this.nameSearch || "").trim();
            var dob = (this.dobSearch || "").trim();
            return name.length >= 2 || this.hasDobValue(dob);
        },
        searchingByDob() {
            return this.hasDobValue(this.dobSearch);
        },
    },
    methods: {
        hasDobValue(value) {
            var digits = (value || "").replace(/\D/g, "");
            return digits.length === 8;
        },
        formatDobQuery(value) {
            var digits = (value || "").replace(/\D/g, "");
            if (digits.length !== 8) {
                return (value || "").trim();
            }
            return digits.substr(0, 2) + "/" + digits.substr(2, 2) + "/" + digits.substr(4, 4);
        },
        onSearch() {
            if (this.selected) {
                return;
            }

            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            this.searchTimer = setTimeout(() => {
                this.loadResults();
            }, 300);
        },
        async loadResults() {
            var name = (this.nameSearch || "").trim();
            var dob = (this.dobSearch || "").trim();
            if (name.length < 2 && !this.hasDobValue(dob)) {
                this.results = [];
                return;
            }

            this.loading = true;

            try {
                const results = await this.$http.get(
                    "api/students/searchStudents?name=" +
                        encodeURIComponent(name) +
                        "&dob=" +
                        encodeURIComponent(this.formatDobQuery(dob)),
                );
                this.results = results || [];
            } catch (e) {
                this.results = [];
            }

            this.loading = false;
        },
        select(student) {
            this.selected = student;
            this.$emit("input", student.accountID);
            this.$emit("selected", this.toAccount(student));
        },
        toAccount(student) {
            if (!student) {
                return null;
            }

            return {
                accountID: student.accountID,
                name: student.name,
                hname: student.hname,
                address1: student.address1,
                homePhone: student.homePhone,
                cellPhone: student.cellPhone,
                emailAddress: student.emailAddress,
                studentName: this.studentDisplayName(student),
            };
        },
        studentDisplayName(student) {
            if (!student) return "";
            var first = student.engName || student.hebName || "";
            var last = student.lastName || student.lastNameH || "";
            return (first + " " + last).trim() || student.studentID || "";
        },
        formatDob(value) {
            if (!value) return "";
            var parsed = moment(value);
            if (!parsed.isValid() || parsed.year() < 1900) {
                return "";
            }
            return parsed.format("MM/DD/YYYY");
        },
        formatPhone(value) {
            return value ? formatPhoneNumber(value) : "";
        },
        removeSelected() {
            this.selected = null;
            this.$emit("input", "");
            this.$emit("selected", null);
        },
        clearSelection() {
            this.selected = null;
        },
        clear() {
            this.nameSearch = "";
            this.dobSearch = "";
            this.results = [];
            this.selected = null;
            this.$emit("input", "");
            this.$emit("selected", null);
        },
    },
};
</script>
<style>
.assign-student-fields {
    margin: 0;
}

.assign-student-results {
    max-height: 220px;
    overflow-y: auto;
}

.assign-student-card {
    cursor: pointer;
    border: 1px solid #e0e0e0;
    transition: border-color 0.15s ease, background 0.15s ease;
}

.assign-student-card:hover {
    border-color: #bdbdbd;
    background: #fafafa;
}

.assign-student-card--selected {
    border-color: #4caf50;
    background: #f1ffdc;
}

.assign-student-dob--match {
    background: #fff3e0;
    color: #e65100;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 3px;
}

.assign-student-selected {
    border: none;
    border-left: 4px solid #4caf50;
    background: #f4fff4;
    box-shadow: 0 2px 6px rgba(76, 175, 80, 0.18);
}

.assign-student-selected-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 6px 0 12px;
}

.assign-student-selected-clear {
    margin: 0;
    height: 28px;
    width: 28px;
}

.assign-student-selected-body {
    padding: 2px 12px 8px;
}

.assign-student-selected-label {
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 11px;
    font-weight: 700;
    color: #2e7d32;
}

.assign-student-selected-name {
    font-size: 14px;
    font-weight: 600;
    color: #263238;
}

.assign-student-selected-meta {
    font-size: 12px;
    font-weight: 400;
    color: #607d8b;
}
</style>
